import React, { useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import styles from './css/index.module.scss';
import Input from '../../../../ui-kit/Input';
import Button, { BUTTON_VARIANTS } from '../../../../ui-kit/Button';
import CompactSelect from '../CompactSelect';
import RwLabel from '../RwLabel';
import ErrorIcon from '../../../../assets/icons/error.svg';
import FilterIcon from '../../../../assets/icons/filter.svg';
import ChevronIcon from '../../../../assets/icons/dropdownRight.svg';
import useSoundproofing from '../../../../hooks/useSoundproofing';
import useTranslations from '../../../../hooks/useTranslations';
import { DICTIONARY } from '../../../../translations/dictionary';
import SoundproofingServices, { RANGE_ERROR_TYPES } from '../../../../services/SoundproofingServices';
import {
  SOUNDPROOFING_RANGE_KEYS,
  SOUNDPROOFING_RANGE_LIMITS,
  SOUNDPROOFING_THICKNESS_OPTIONS,
} from '../../../../constants/soundproofing';

const FILTER_RANGE_KEYS = [SOUNDPROOFING_RANGE_KEYS.rw, SOUNDPROOFING_RANGE_KEYS.rwC, SOUNDPROOFING_RANGE_KEYS.rwCtr];
const INPUT_STYLE = { height: '36px', padding: '0 14px' };

function getInputValue(ev) {
  return typeof ev === 'string' ? ev : ev?.target?.value || '';
}

function FilterRange({
  rangeKey, value, error, onChange, onKeyDown,
}) {
  const { t } = useTranslations();
  const { min, max } = SOUNDPROOFING_RANGE_LIMITS[rangeKey];

  const errorText = useMemo(() => {
    if (!error) return '';
    if (error.type === RANGE_ERROR_TYPES.min) return t(DICTIONARY.soundproofingPage.minAllowed, min);
    if (error.type === RANGE_ERROR_TYPES.max) return t(DICTIONARY.soundproofingPage.maxAllowed, max);
    return t(DICTIONARY.soundproofingPage.allowedRange, min, max);
  }, [error, min, max, t]);

  const onInputChange = (key) => (ev) => {
    onChange({ ...value, [key]: SoundproofingServices.sanitizeNumberInput(getInputValue(ev)) });
  };

  return (
    <div className={styles.field}>
      <div className={styles.fieldHeader}>
        <p className={styles.fieldTitle}><RwLabel rangeKey={rangeKey} /></p>
        {error && (
          <div className={styles.error}>
            <img src={ErrorIcon} alt="!" className={styles.errorIcon} />
            <div className={styles.errorTooltip}>
              <p className={styles.errorTooltipText}>{errorText}</p>
            </div>
          </div>
        )}
      </div>
      <div className={styles.rangeInputs}>
        {['from', 'to'].map((key) => (
          <Input
            key={key}
            width="calc(50% - 4px)"
            style={INPUT_STYLE}
            inputMode="decimal"
            placeholder={t(DICTIONARY.soundproofingPage[key])}
            value={value[key]}
            onChange={onInputChange(key)}
            onKeyDown={onKeyDown}
            error={error?.[key] ? 'NO_ERROR_TEXT' : ''}
          />
        ))}
      </div>
    </div>
  );
}

function ResultsFilters({ isMobile }) {
  const { t } = useTranslations();
  const {
    resultsFilters, resultsLoading, applyResultsFilters, resetResultsFilters,
  } = useSoundproofing();
  const [draft, setDraft] = useState(resultsFilters);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    setDraft(resultsFilters);
  }, [resultsFilters]);

  const errors = useMemo(() => SoundproofingServices.getResultsFiltersErrors(draft), [draft]);
  const isChanged = JSON.stringify(draft) !== JSON.stringify(resultsFilters);
  const canApply = isChanged && !Object.keys(errors).length && !resultsLoading;
  const canReset = SoundproofingServices.areResultsFiltersChanged(resultsFilters)
    || SoundproofingServices.areResultsFiltersChanged(draft);

  const thicknessItems = useMemo(() => [
    { value: null, label: t(DICTIONARY.soundproofingPage.any) },
    ...SOUNDPROOFING_THICKNESS_OPTIONS.results.map((option) => ({ value: option, label: option })),
  ], [t]);

  const changeDraft = (key, value) => {
    setDraft((prev) => ({ ...prev, [key]: value }));
  };

  const onApplyClick = () => {
    if (canApply) applyResultsFilters(draft);
  };

  const onResetClick = () => {
    if (!canReset) return;
    setDraft(SoundproofingServices.getDefaultResultsFilters());
    resetResultsFilters();
  };

  const onKeyDown = (ev) => {
    if (ev.key === 'Enter') onApplyClick();
  };

  const isBodyVisible = !isMobile || isExpanded;

  return (
    <div className={styles.filters}>
      <div className={styles.header}>
        <div
          className={classNames(styles.headerTitle, { pressable: isMobile })}
          onClick={isMobile ? () => setIsExpanded((prev) => !prev) : undefined}
        >
          <img src={FilterIcon} alt="" className={styles.filterIcon} />
          <p className={styles.headerTitleText}>{t(DICTIONARY.soundproofingPage.filters)}</p>
          {isMobile && (
            <img
              src={ChevronIcon}
              alt=">"
              className={classNames(styles.chevron, { [styles.chevronExpanded]: isExpanded })}
            />
          )}
        </div>
        {!isMobile && (
          <div
            className={classNames({ pressable: canReset })}
            onClick={onResetClick}
          >
            <p className={classNames(styles.reset, { [styles.resetActive]: canReset })}>
              {t(DICTIONARY.soundproofingPage.resetAll)}
            </p>
          </div>
        )}
      </div>

      {isBodyVisible && (
        <div className={styles.body}>
          <div className={styles.field}>
            <p className={styles.fieldTitle}>{t(DICTIONARY.soundproofingPage.composition)}</p>
            <Input
              width="100%"
              style={INPUT_STYLE}
              placeholder={t(DICTIONARY.soundproofingPage.any)}
              value={draft.composition}
              onChange={(ev) => changeDraft('composition', getInputValue(ev))}
              onKeyDown={onKeyDown}
            />
          </div>

          <div className={styles.field}>
            <p className={styles.fieldTitle}>{t(DICTIONARY.soundproofingPage.thickness)}</p>
            <CompactSelect
              width="100%"
              items={thicknessItems}
              value={draft.thickness}
              onChange={(value) => changeDraft('thickness', value)}
              placeholder={t(DICTIONARY.soundproofingPage.any)}
            />
          </div>

          {FILTER_RANGE_KEYS.map((key) => (
            <FilterRange
              key={key}
              rangeKey={key}
              value={draft[key]}
              error={errors[key]}
              onChange={(value) => changeDraft(key, value)}
              onKeyDown={onKeyDown}
            />
          ))}

          <div className={styles.buttons}>
            <Button
              width="100%"
              text={t(DICTIONARY.soundproofingPage.apply)}
              isDisabled={!canApply}
              onClick={onApplyClick}
            />
            {isMobile && (
              <Button
                width="100%"
                variant={BUTTON_VARIANTS.secondary}
                text={t(DICTIONARY.soundproofingPage.resetAll)}
                isDisabled={!canReset}
                onClick={onResetClick}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default ResultsFilters;
